import { AgencyUserFieldsFragment } from '~/api/graphql';

/**
 * A null userId means offboarded; an unresolvable one means invisible to this
 * caller — hence two different fallbacks.
 */
export const formatAgencyUserName = (
  userId: string | null | undefined,
  agencyUsers: Pick<AgencyUserFieldsFragment, 'id' | 'name' | 'email'>[]
): string => {
  if (!userId) {
    return 'Offboarded user';
  }
  const user = agencyUsers.find((candidate) => candidate.id === userId);
  return user?.name?.trim() || user?.email?.trim() || 'Unknown';
};

/** Date alone, e.g. "Oct 3, 2026". */
export const dateFormatter = new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' });

/** Date and time: "has this gone out yet" is unanswerable for today's rows without the time. */
export const rowDateTimeFormatter = new Intl.DateTimeFormat('en-US', {
  dateStyle: 'medium',
  timeStyle: 'short'
});

const relativeFormatter = new Intl.RelativeTimeFormat('en-US', { numeric: 'auto' });

const RELATIVE_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 24 * 60 * 60],
  ['month', 30 * 24 * 60 * 60],
  ['week', 7 * 24 * 60 * 60],
  ['day', 24 * 60 * 60],
  ['hour', 60 * 60],
  ['minute', 60]
];

/** "5 minutes ago", "yesterday", "last month" -- relative at every distance. */
export const formatRelativeTime = (at: Date, now: Date = new Date()): string => {
  const seconds = Math.round((now.getTime() - at.getTime()) / 1000);
  for (const [unit, size] of RELATIVE_UNITS) {
    if (Math.abs(seconds) >= size) {
      return relativeFormatter.format(-Math.round(seconds / size), unit);
    }
  }
  return 'just now';
};
